/**
 * The rates this app prices with, fetched from the API instead of bundled.
 *
 * Before this, the booking app's prices came from constants compiled into the
 * JavaScript bundle. That has a failure mode beyond "an admin cannot change
 * them": a customer with a cached bundle keeps quoting yesterday's prices
 * indefinitely, and the first they or we learn of it is checkout rejecting the
 * posted total after the card has been charged. Fetching means a stale client is
 * stale for one page load, not for as long as its cache lives.
 *
 * The shape is deliberately narrow — base prices, day-of-week surge and the
 * Flexible Time discount, current values only. Same-Day is not here; it is asked
 * about one date at a time, because whether it is open depends on the time in New
 * York and only the server may answer that. See
 * `docs/adr/0006-pricing-config-is-public-integrity-is-not.md` and
 * `docs/adr/0004-same-day-availability-server-side-only.md`.
 */

import {
  PricingConfig,
  PublicPricingConfig,
  defaultPricingConfig,
  fromPublicPricingConfig,
} from "@homero/pricing";

/**
 * The config in force, or null until the fetch lands.
 *
 * Module state rather than React context because `lib/price.ts` is called from
 * reducers and plain functions as well as components, and threading a hook
 * through those would have meant a second price path for the non-component
 * callers — which is the exact duplication this app spent five implementations
 * learning to avoid.
 */
let fetched: PricingConfig | null = null;
let inFlight: Promise<PricingConfig> | null = null;
/**
 * The server's fingerprint of the config `fetched` came from.
 *
 * Held separately because it is not a rate and so is not part of
 * `PricingConfig` — it is an opaque token this app echoes back at checkout so
 * the server can tell whether an admin changed a price while this tab was open.
 * See `pricingConfigFingerprint`.
 */
let fetchedVersion: string | null = null;
/** Whether the last attempt to load rates failed. Drives the checkout block. */
let lastFailedAt: number | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * The rates to price with right now.
 *
 * Falls back to the bundled card until the fetch resolves, so the first paint
 * shows a price rather than a spinner. The fallback carries zero surge, which
 * means the worst case is a price that is *lower* than the configured one for a
 * few hundred milliseconds — never a booking charged at the fallback, because the
 * server re-prices from its own config at checkout.
 */
export function currentPricingConfig(): PricingConfig {
  return fetched ?? defaultPricingConfig(new Date());
}

/** Whether the API's rates have arrived, for callers that want to wait. */
export function pricingConfigIsLive(): boolean {
  return fetched !== null;
}

/**
 * The fingerprint of the rates in use, or null while on the bundled fallback.
 *
 * Sent with the booking at checkout. Null is deliberately *not* a value the
 * server will accept as agreement — a client on the fallback must not reach
 * checkout at all (see `pricingConfigLoadFailed`), and the server's total
 * comparison catches it if one somehow does.
 */
export function pricingConfigVersion(): string | null {
  return fetchedVersion;
}

/**
 * Whether loading the rates failed and we are still on the bundled fallback.
 *
 * The distinction that matters at checkout: "not loaded yet" is a moment, and
 * "could not load" is a state a customer can sit in indefinitely while the cart
 * shows the bundled prices — no surge, no Same-Day, whatever the bundle was
 * built with. Quoting from that and then submitting is precisely how a card gets
 * charged the server's total and the booking is dropped for disagreeing with the
 * client's. Checkout is blocked while this is true.
 */
export function pricingConfigLoadFailed(): boolean {
  return fetched === null && lastFailedAt !== null;
}

/** Subscribe to the moment fetched rates replace the bundled fallback. */
export function onPricingConfigChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Fetch the published rates once.
 *
 * Deduplicated: several components mount at once in this funnel, and each of
 * them asking would be several identical requests racing to set the same value.
 */
export function loadPricingConfig(force = false): Promise<PricingConfig> {
  // `force` re-asks even with rates in hand, keeping them in use until the new
  // ones land — for checkout, after the server says the price has moved.
  if (fetched && !force) return Promise.resolve(fetched);
  if (inFlight) return inFlight;

  inFlight = fetch(`${process.env.REACT_APP_API_URL}/pricing/config`)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Pricing config request failed: ${response.status}`);
      }
      const published = (await response.json()) as PublicPricingConfig;
      /**
       * Validated on arrival, not on first use. `fromPublicPricingConfig` throws
       * on a payload a bad admin edit or a deploy skew made invalid, and it is
       * far better to find that out here — where the fallback is still in place —
       * than one keystroke later, inside a total the customer is reading.
       */
      const config = fromPublicPricingConfig(published);
      fetched = config;
      fetchedVersion = published.configVersion ?? null;
      lastFailedAt = null;
      /**
       * Cleared once settled. Only concurrent callers should share a request;
       * a forced reload later must make a new one. Left set, `force` handed back
       * this resolved promise — the old rates and the old fingerprint — so after
       * a 409 the app re-quoted from the rates the server had just refused, and
       * the next attempt was refused again.
       */
      inFlight = null;
      listeners.forEach((listener) => listener());
      return config;
    })
    .catch((error) => {
      // Cleared so a later attempt can retry rather than being wedged on a
      // rejected promise forever.
      inFlight = null;
      lastFailedAt = Date.now();
      /**
       * Listeners are notified on failure too, not only on success. Checkout is
       * gated on `pricingConfigLoadFailed`, and a component that mounted before
       * the request failed has no other way to learn that it did — so without
       * this the block would not appear until something unrelated re-rendered.
       */
      listeners.forEach((listener) => listener());
      throw error;
    });

  return inFlight;
}

export interface SameDayAnswer {
  serviceDate: string;
  isSameDay: boolean;
  available: boolean;
  reason: string | null;
  windows: Array<{
    id: string;
    label: string;
    startMinutes: number;
    endMinutes: number;
  }>;
  /** The surcharge for this date and cleaning type, when Same-Day is open. */
  surchargePercent: number | null;
}

/**
 * Ask whether Same-Day is open for a service date.
 *
 * A request rather than a calculation, and not cached. The answer depends on the
 * current time in New York and narrows through the day, so a cached "open" would
 * keep offering a window after it closed. This app cannot work it out locally on
 * purpose: it holds no cutoffs, and its own clock is the customer's.
 */
export async function fetchSameDayAvailability(
  serviceDate: string,
  cleaningType?: string
): Promise<SameDayAnswer> {
  const params = new URLSearchParams({ date: serviceDate });
  if (cleaningType) params.set("cleaning_type", cleaningType);

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/pricing/same-day?${params.toString()}`
  );
  if (!response.ok) {
    throw new Error(`Same-Day availability request failed: ${response.status}`);
  }
  return (await response.json()) as SameDayAnswer;
}

/** Test seam: drop the fetched config so a test starts from the fallback. */
export function resetPricingConfigForTests(): void {
  fetched = null;
  inFlight = null;
  fetchedVersion = null;
  lastFailedAt = null;
  listeners.clear();
}
